import * as Sentry from "@sentry/browser";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AnyAction } from "redux-saga";
import { ThunkDispatch } from "redux-thunk";

import getWorker from "./worker-factory";
import {
  setDictationGlobalStatus,
  setDictationThrottledLoadingProgress,
} from "../../actions/sysActions";
import { FeatureFlags } from "../../reducers/sysReducer";

const browserSupported =
  !!navigator.mediaDevices.getUserMedia && !!(navigator as any).gpu;

const DictationPreloader = () => {
  const dispatch = useDispatch() as ThunkDispatch<any, any, AnyAction>;
  const features: FeatureFlags = useSelector(({ sys }: any) => sys.features);

  const worker = useRef<SharedWorker | null>(null);
  const progressCount = useRef(0);

  interface ProgressItem {
    file?: string;
    loaded?: number;
    name?: string;
    progress?: number;
    status?: string;
    total?: number;
  }

  let progressItems: ProgressItem[] = [];

  // setup the worker as soon as the component is mounted.
  useEffect(() => {
    if (browserSupported && features?.dictation) {
      if (!worker.current) {
        // Create the worker if it does not yet exist.
        worker.current = getWorker();
      }

      // Store states that pertain to all dictation instances in redux.
      const onMessageReceived = (e: any, dispatch: Function) => {
        switch (e.data.status) {
          case "loading":
            // Model file start load: add a new progress item to the list.
            dispatch(setDictationGlobalStatus("loading"));
            break;

          case "initiate":
            progressItems = [...progressItems, e.data];
            break;

          case "progress":
            // Model file progress: update one of the progress items.
            progressItems = progressItems.map((item) => {
              if (item.file === e.data.file) {
                return { ...item, ...e.data };
              }
              return item;
            });

            progressCount.current += 1;

            // Only disaptch progress updates every 60 items. This prevents spamming redux with actions. Also dispatch after the first 20 items, which gives the ueer a sense of progress.
            if (
              progressCount.current === 20 ||
              progressCount.current % 60 === 0
            ) {
              // Calculate weighted progress
              const totalSize = progressItems.reduce(
                (sum: number, item: ProgressItem) => sum + (item.total ?? 0),
                0
              );
              const totalLoaded = progressItems.reduce(
                (sum: number, item: ProgressItem) => sum + (item.loaded ?? 0),
                0
              );
              const overallProgress = (totalLoaded / totalSize) * 100;

              dispatch(setDictationThrottledLoadingProgress(overallProgress));
            }

            break;

          case "loaded":
            // Pipeline ready: the worker is ready to warm the model.
            dispatch(setDictationGlobalStatus("loaded"));
            break;

          case "error":
            console.debug(e);
            dispatch(setDictationGlobalStatus("error"));
            if (e.data.error) {
              console.error(e.data.error);
              Sentry.captureException(e.data.error);
            }
            break;

          case "ready":
            // Model warmed: the worker is ready to accept messages.
            dispatch(setDictationGlobalStatus("ready"));
            break;
        }
      };

      worker.current.port.onmessage = (event) => {
        onMessageReceived(event, dispatch);
      };

      // Load the data model when the component is mounted.
      worker.current?.port.postMessage({ type: "load" });

      // Define a cleanup function for when the component is unmounted.
      return () => {
        worker.current?.port.postMessage({ type: "close" });
        worker.current = null;
      };
    }
  }, []);

  return null;
};

export default DictationPreloader;
